// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { fetchAllActivePlacements, NAME_BY_TYPE } from '../utils/applianceQueries'
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts'
import { QRCodeSVG } from 'qrcode.react'
import CasavioLogo from './shared/CasavioLogo'
import OwnerSandbox from './OwnerSandbox'

const OWNER_ID = '26680bcf-f67b-490e-ba08-e1f25ddcfd01'

const C = {
  bg: '#F2EDE4', border: '#DDD4C6',
  amber: '#B8721F', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A',
  danger: '#B03020', blue: '#2563EB',
  dark: '#1C1209', card: '#FEFCF8',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const inputStyle = {
  flex: 1, minWidth: '140px', padding: '6px 10px',
  border: `1px solid ${C.border}`, borderRadius: '6px',
  background: 'white', fontFamily: fs, fontSize: '13px', color: C.brown, outline: 'none',
}
const selectStyle = {
  padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: '6px',
  background: 'white', fontFamily: fs, fontSize: '13px', color: C.brown, outline: 'none',
}
const btnStyle = (color) => ({
  background: color, color: 'white', border: 'none', borderRadius: '6px',
  padding: '6px 14px', cursor: 'pointer', fontSize: '12px', fontFamily: fs, fontWeight: '600',
})

const TASK_CATEGORIES = ['Legal', 'Financial', 'Marketing', 'Product', 'Launch', 'Product (current)', 'Product (vision)', 'Beta Launch']
const FEATURE_COLS = ['backlog', 'planned', 'in_progress', 'shipped']
const FEATURE_COL_LABELS = { backlog: 'Backlog', planned: 'Planned', in_progress: 'In Progress', shipped: 'Shipped' }
const FEATURE_COL_COLORS = { backlog: C.muted, planned: C.amber, in_progress: C.blue, shipped: C.success }

function SectionCard({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', marginBottom: '20px', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px', cursor: 'pointer',
          background: open ? 'transparent' : '#FAF7F2',
          borderBottom: open ? `1px solid ${C.border}` : 'none',
        }}
      >
        <h2 style={{ margin: 0, fontFamily: f, fontSize: '15px', color: C.brown }}>{title}</h2>
        <span style={{ color: C.muted, fontSize: '12px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding: '16px 20px 20px' }}>{children}</div>}
    </div>
  )
}

function StatTile({ label, value, onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px',
        padding: '14px 18px', flex: 1, minWidth: '110px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
        boxShadow: hovered ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      <div style={{ fontFamily: f, fontSize: '26px', fontWeight: '700', color: C.brown, display: 'flex', alignItems: 'baseline', gap: '4px' }}>
        {value}
        {onClick && (
          <span style={{ fontSize: '13px', color: C.amber, opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>→</span>
        )}
      </div>
      <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '3px' }}>{label}</div>
    </div>
  )
}

// ── Users tile (read-only) ────────────────────────────────────────────────
// Reads auth.users via the owner-gated list-real-users EF, fresh on every mount
// (no caching — the user base is tiny). v1 is READ-ONLY: no delete, no edit.
function relTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  if (diff < 0) return 'just now'
  const days = Math.floor(diff / 86400000)
  if (days < 1) {
    const hrs = Math.floor(diff / 3600000)
    if (hrs < 1) { const m = Math.floor(diff / 60000); return m <= 1 ? 'just now' : `${m}m ago` }
    return `${hrs}h ago`
  }
  if (days < 7) return `${days}d ago`
  if (days < 35) return `${Math.floor(days / 7)}w ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
// "Never" when the only sign-in is within 60s of account creation (i.e. the
// signup itself) or there is no sign-in at all.
function lastSignInLabel(u) {
  if (!u.last_sign_in_at) return 'Never'
  const created = new Date(u.created_at).getTime()
  const last = new Date(u.last_sign_in_at).getTime()
  if (Math.abs(last - created) < 60000) return 'Never'
  return relTime(u.last_sign_in_at)
}

function UsersTile() {
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('real') // 'real' | 'active' | 'all'
  const [copied, setCopied] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 720 : false)

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 720)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const load = async () => {
    setLoading(true); setError(null)
    try {
      // The supabase client attaches the logged-in owner's JWT automatically;
      // the EF enforces the owner check server-side.
      const { data, error: invErr } = await supabase.functions.invoke('list-real-users')
      if (invErr) {
        const status = invErr?.context?.status
        setError(status === 403 ? 'Owner access required' : (invErr.message || 'Failed to load users'))
        setRows(null)
      } else {
        setRows(Array.isArray(data?.users) ? data.users : [])
      }
    } catch (e) {
      setError(String(e?.message || e)); setRows(null)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, []) // fresh fetch on every mount; no caching

  const copyId = (id, e) => {
    if (e) e.stopPropagation()
    navigator.clipboard?.writeText(id)
    setCopied(id)
    setTimeout(() => setCopied(c => (c === id ? null : c)), 1500)
  }

  const all = rows || []
  const testCount = all.filter(u => u.is_test).length
  const realCount = all.length - testCount

  const isActive30 = (u) =>
    u.days_since_last_sign_in != null
      ? u.days_since_last_sign_in <= 30
      : (Date.now() - new Date(u.created_at).getTime()) <= 30 * 86400000

  const shown = all.filter(u =>
    filter === 'real' ? !u.is_test : filter === 'active' ? isActive30(u) : true
  )

  const FILTERS = [
    { key: 'real', label: 'Real users' },
    { key: 'active', label: 'Active 30d' },
    { key: 'all', label: 'All' },
  ]

  const countBadge = (n) => (
    <span style={{
      display: 'inline-block', minWidth: 18, textAlign: 'center',
      fontFamily: fs, fontSize: '12px', fontWeight: n === 0 ? '600' : '700',
      color: n === 0 ? C.muted : C.brown,
      background: n === 0 ? C.bg : 'transparent',
      border: n === 0 ? `1px solid ${C.border}` : 'none',
      borderRadius: '10px', padding: n === 0 ? '1px 8px' : '0',
    }}>{n}</span>
  )

  const th = { textAlign: 'left', fontFamily: fs, fontSize: '10px', fontWeight: '700', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', padding: '0 10px 8px', whiteSpace: 'nowrap' }
  const td = { fontFamily: fs, fontSize: '13px', color: C.brown, padding: '9px 10px', borderTop: `1px solid ${C.border}`, whiteSpace: 'nowrap' }

  return (
    <SectionCard title="👥 Users">
      {loading && (
        <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, padding: '8px 0' }}>Loading users…</div>
      )}
      {!loading && error && (
        <div style={{ fontFamily: fs, fontSize: '13px', color: C.danger, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>{error}</span>
          <button onClick={load} style={{ background: C.amber, color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: fs, fontWeight: '600' }}>Retry</button>
        </div>
      )}
      {!loading && !error && (
        <>
          <div style={{ fontFamily: fs, fontSize: '13px', color: C.brown, marginBottom: '12px' }}>
            <strong>{realCount}</strong> real {realCount === 1 ? 'user' : 'users'} · <strong>{testCount}</strong> test · <strong>{all.length}</strong> total
          </div>

          <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden', marginBottom: '14px' }}>
            {FILTERS.map((ff, i) => (
              <button
                key={ff.key}
                onClick={() => setFilter(ff.key)}
                style={{
                  background: filter === ff.key ? C.amber : 'transparent',
                  color: filter === ff.key ? '#fff' : C.muted,
                  border: 'none', borderLeft: i === 0 ? 'none' : `1px solid ${C.border}`,
                  padding: '7px 14px', cursor: 'pointer', fontFamily: fs, fontSize: '12px',
                  fontWeight: filter === ff.key ? '700' : '600',
                }}
              >{ff.label}</button>
            ))}
          </div>

          {shown.length === 0 && (
            <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted }}>No users in this view.</div>
          )}

          {/* Desktop: full table. Row click copies user_id. */}
          {!narrow && shown.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={th}>Email</th><th style={th}>Created</th><th style={th}>Last sign-in</th>
                    <th style={th}>Days</th><th style={th}>Homes</th><th style={th}>Props</th><th style={th}>Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(u => (
                    <tr
                      key={u.user_id}
                      onClick={() => copyId(u.user_id)}
                      title="Click to copy user_id"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.bg }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                    >
                      <td style={td}>
                        {u.email}
                        {u.is_test && <span style={{ marginLeft: 6, fontSize: '9px', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '1px 6px', fontFamily: fs }}>TEST</span>}
                        {copied === u.user_id && <span style={{ marginLeft: 8, fontSize: '11px', color: C.success, fontFamily: fs }}>✓ copied</span>}
                      </td>
                      <td style={td}>{relTime(u.created_at)}</td>
                      <td style={td}>{lastSignInLabel(u)}</td>
                      <td style={{ ...td, color: (u.days_since_last_sign_in != null && u.days_since_last_sign_in > 30) ? C.muted : C.brown }}>
                        {u.days_since_last_sign_in == null ? '—' : u.days_since_last_sign_in}
                      </td>
                      <td style={td}>{countBadge(u.homes_count)}</td>
                      <td style={td}>{countBadge(u.properties_count)}</td>
                      <td style={td}>{countBadge(u.emergency_contacts_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile: collapsed rows (email + last sign-in); tap to expand detail. */}
          {narrow && shown.length > 0 && (
            <div>
              <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginBottom: '8px' }}>{shown.length} {shown.length === 1 ? 'row' : 'rows'}</div>
              {shown.map(u => (
                <div key={u.user_id} style={{ borderTop: `1px solid ${C.border}`, padding: '10px 0' }}>
                  <div onClick={() => setExpanded(x => x === u.user_id ? null : u.user_id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: fs, fontSize: '13px', color: C.brown, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.email}{u.is_test && <span style={{ marginLeft: 6, fontSize: '9px', color: C.muted }}>TEST</span>}
                      </div>
                      <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '2px' }}>Last: {lastSignInLabel(u)}</div>
                    </div>
                    <span style={{ color: C.muted, fontSize: '12px' }}>{expanded === u.user_id ? '▲' : '▼'}</span>
                  </div>
                  {expanded === u.user_id && (
                    <div style={{ marginTop: '8px', fontFamily: fs, fontSize: '12px', color: C.brown, lineHeight: 1.7 }}>
                      <div>Created: {relTime(u.created_at)}</div>
                      <div>Days since sign-in: {u.days_since_last_sign_in == null ? '—' : u.days_since_last_sign_in}</div>
                      <div>Homes: {u.homes_count} · Properties: {u.properties_count} · Contacts: {u.emergency_contacts_count}</div>
                      <button onClick={(e) => copyId(u.user_id, e)} style={{ marginTop: '8px', background: copied === u.user_id ? C.success : C.bg, color: copied === u.user_id ? '#fff' : C.brown, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontFamily: fs, cursor: 'pointer' }}>
                        {copied === u.user_id ? '✓ Copied user_id' : 'Copy user_id'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

export default function OwnerDashboard({ session }) {
  const [metrics, setMetrics] = useState({ users: 0, homes: 0, appliances: 0, systems: 0, manualsFound: 0, tasksCompleted: 0, newThisMonth: 0, pendingRequests: 0, signupsByMonth: [], totalJobs: 0, emergencyJobs: 0, wishlistJobs: 0 })
  const [config, setConfig] = useState({})
  const [tasks, setTasks] = useState([])
  const [betaUsers, setBetaUsers] = useState([])
  const [features, setFeatures] = useState([])
  const [loading, setLoading] = useState(true)

  const [newTask, setNewTask] = useState({ task: '', category: 'Product' })
  const [showAddTask, setShowAddTask] = useState(false)
  const [newBetaUser, setNewBetaUser] = useState({ name: '', email: '', status: 'invited', source: '', nps_score: '', notes: '', referred_by: null, referred_by_user_id: null })
  const [showAddBeta, setShowAddBeta] = useState(false)
  const [editingBetaUser, setEditingBetaUser] = useState(null)
  const [editBetaUserData, setEditBetaUserData] = useState({})
  const [newFeature, setNewFeature] = useState({ title: '', description: '' })
  const [showAddFeature, setShowAddFeature] = useState(false)

  // External Services cluster — id of the pending vendor whose context
  // panel is currently expanded. Null = nothing expanded. Tap-to-reveal
  // pattern is used on both desktop and mobile (no platform detection) so
  // the experience is consistent: first tap reveals context, second tap on
  // "Continue to signup" follows the link. Active vendors don't use this
  // state — they're direct anchors.
  const [expandedPendingVendor, setExpandedPendingVendor] = useState(null)

  // AI Calibration Sandbox — when true, the /owner page body is replaced
  // with the OwnerSandbox sub-view (Quick Launch Bar stays visible at top
  // for navigation). The sub-view's own back button toggles this false.
  const [showSandbox, setShowSandbox] = useState(false)

  const [news, setNews] = useState([])
  const [newsLoading, setNewsLoading] = useState(false)
  const [newsSettings, setNewsSettings] = useState(null)
  const [newsSaved, setNewsSaved] = useState(false)
  const [testEmailSending, setTestEmailSending] = useState(false)
  const [testEmailResult, setTestEmailResult] = useState(null)

  const [drilldown, setDrilldown] = useState(null)
  const [copiedId, setCopiedId] = useState(null)
  const [pendingContractors, setPendingContractors] = useState([])
  const [referralCodes, setReferralCodes] = useState([])
  const [bugReports, setBugReports] = useState([])

  useEffect(() => {
    window.history.pushState({}, '', '/owner')
    return () => window.history.pushState({}, '', '/')
  }, [])

  useEffect(() => { loadAll() }, [])
  useEffect(() => { fetchNews() }, [])
  useEffect(() => {
    if (config.news_settings && !newsSettings) setNewsSettings(config.news_settings)
  }, [config.news_settings])

  async function loadAll() {
    try {
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
      const [
        { count: userCount },
        { count: homeCount },
        { count: applianceCount },
        { count: systemCount },
        { count: manualCount },
        { count: logCount },
        { count: newThisMonthCount },
        { count: pendingRequestsCount },
        { data: profilesData },
        { data: configData },
        { data: tasksData },
        { data: betaData },
        { data: featuresData },
        { data: contractorsData },
        { data: referralData },
        { data: bugReportsData },
        { count: totalJobsCount },
        { count: emergencyJobsCount },
        { count: wishlistJobsCount },
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('homes').select('*', { count: 'exact', head: true }),
        supabase.from('appliance_placements').select('id', { count: 'exact', head: true }).is('removed_date', null),
        supabase.from('systems').select('*', { count: 'exact', head: true }),
        supabase.from('appliance_placements').select('id, slot:appliance_slots!inner(manual_url)', { count: 'exact', head: true }).is('removed_date', null).not('slot.manual_url', 'is', null).not('slot.manual_url', 'ilike', '%google.com%'),
        supabase.from('maintenance_logs').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', startOfMonth),
        supabase.from('contractor_requests').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('profiles').select('created_at').order('created_at'),
        supabase.from('owner_config').select('*'),
        supabase.from('owner_tasks').select('*').order('created_at'),
        supabase.from('beta_users').select('*').order('created_at', { ascending: false }),
        supabase.from('feature_requests').select('*').order('votes', { ascending: false }),
        supabase.from('contractors').select('*').eq('status', 'pending').order('created_at', { ascending: false }),
        supabase.from('referral_codes').select('*').order('created_at', { ascending: false }),
        supabase.from('bug_reports').select('*, profiles(first_name, last_name, email)').order('created_at', { ascending: false }).limit(50),
        supabase.from('jobs').select('*', { count: 'exact', head: true }).neq('status', 'complete'),
        supabase.from('jobs').select('*', { count: 'exact', head: true }).eq('urgency', 'emergency').neq('status', 'complete'),
        supabase.from('jobs').select('*', { count: 'exact', head: true }).eq('urgency', 'wishlist').neq('status', 'complete'),
      ])

      const configMap = {}
      for (const row of configData || []) configMap[row.key] = row.value

      setMetrics({
        users: userCount || 0,
        homes: homeCount || 0,
        appliances: applianceCount || 0,
        systems: systemCount || 0,
        manualsFound: manualCount || 0,
        tasksCompleted: logCount || 0,
        newThisMonth: newThisMonthCount || 0,
        pendingRequests: pendingRequestsCount || 0,
        signupsByMonth: buildSignupsByMonth(profilesData || []),
        totalJobs: totalJobsCount || 0,
        emergencyJobs: emergencyJobsCount || 0,
        wishlistJobs: wishlistJobsCount || 0,
      })
      setConfig(configMap)
      setTasks(tasksData || [])
      setBetaUsers(betaData || [])
      setFeatures(featuresData || [])
      setPendingContractors(contractorsData || [])
      setReferralCodes(referralData || [])
      setBugReports(bugReportsData || [])
    } catch (e) {
      console.error('OwnerDashboard load error:', e)
    } finally {
      setLoading(false)
    }
  }

  async function approveContractor(id) {
    await supabase.from('contractors').update({ status: 'approved' }).eq('id', id)
    setPendingContractors(prev => prev.filter(c => c.id !== id))
  }

  async function rejectContractor(id) {
    await supabase.from('contractors').update({ status: 'rejected' }).eq('id', id)
    setPendingContractors(prev => prev.filter(c => c.id !== id))
  }

  function buildSignupsByMonth(profiles) {
    const counts = {}
    const now = new Date()
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.toLocaleString('default', { month: 'short' })} '${String(d.getFullYear()).slice(-2)}`
      counts[key] = 0
    }
    for (const p of profiles) {
      const d = new Date(p.created_at)
      const key = `${d.toLocaleString('default', { month: 'short' })} '${String(d.getFullYear()).slice(-2)}`
      if (key in counts) counts[key]++
    }
    return Object.entries(counts).map(([month, signups]) => ({ month, signups }))
  }

  async function fetchNews() {
    setNewsLoading(true)
    const { data: cacheRow } = await supabase
      .from('owner_config').select('value').eq('key', 'news_cache').single()
    if (cacheRow?.value?.fetched_at) {
      const cacheHours = (cacheRow.value.cache_hours || 4) * 60 * 60 * 1000
      const age = Date.now() - new Date(cacheRow.value.fetched_at).getTime()
      if (age < cacheHours) {
        setNews(cacheRow.value.articles || [])
        setNewsLoading(false)
        return
      }
    }
    try {
      const { data, error } = await supabase.functions.invoke('send-news-briefing', { body: { mode: 'feed' } })
      if (error) throw error
      if (data?.articles?.length) {
        setNews(data.articles)
        await supabase.from('owner_config').upsert(
          { key: 'news_cache', value: { articles: data.articles, fetched_at: new Date().toISOString() } },
          { onConflict: 'key' }
        )
      }
    } catch (err) {
      console.error('fetchNews failed:', err)
    }
    setNewsLoading(false)
  }

  async function saveNewsSettings() {
    await supabase.from('owner_config').upsert(
      { key: 'news_settings', value: newsSettings },
      { onConflict: 'key' }
    )
    setConfig(prev => ({ ...prev, news_settings: newsSettings }))
    setNewsSaved(true)
    setTimeout(() => setNewsSaved(false), 2000)
  }

  async function sendTestEmail() {
    setTestEmailSending(true)
    setTestEmailResult(null)
    try {
      const { data, error } = await supabase.functions.invoke('send-news-briefing', {
        body: { mode: 'email' }
      })
      if (error) throw error
      setTestEmailResult({ ok: true, message: 'Email sent! Check kennethkrummes@gmail.com' })
    } catch (err) {
      setTestEmailResult({ ok: false, message: 'Failed: ' + err.message })
    }
    setTestEmailSending(false)
    setTimeout(() => setTestEmailResult(null), 5000)
  }

  async function openDrilldown(type) {
    const titles = {
      users: 'Total Users', homes: 'Properties', appliances: 'Appliances',
      systems: 'Home Systems', manuals: 'Manuals Found', logs: 'Tasks Completed',
      newthismonth: 'New This Month', avgappliances: 'Avg Appliances per Home',
    }
    setDrilldown({ title: titles[type] || type, loading: true, columns: [], rows: [], note: null })

    try {
      let columns = [], rows = [], note = null

      if (type === 'users' || type === 'newthismonth') {
        const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
        let query = supabase.from('profiles').select('*').order('created_at', { ascending: false })
        if (type === 'newthismonth') query = query.gte('created_at', startOfMonth)
        const [{ data: profiles }, { data: hm }] = await Promise.all([
          query,
          supabase.from('home_members').select('user_id, home_id'),
        ])
        const countMap = {}
        for (const m of hm || []) countMap[m.user_id] = (countMap[m.user_id] || 0) + 1
        columns = ['Name', 'Email', 'Created', 'Properties']
        rows = (profiles || []).map(p => ({
          name: p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || '—',
          email: p.email || '—',
          created: new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          properties: countMap[p.id] || 0,
        }))
      }

      if (type === 'homes') {
        const [{ data: homes }, { data: hm }, { data: profiles }, apps] = await Promise.all([
          supabase.from('homes').select('*').order('created_at', { ascending: false }),
          supabase.from('home_members').select('home_id, user_id'),
          supabase.from('profiles').select('id, full_name, first_name, last_name'),
          fetchAllActivePlacements(supabase),
        ])
        const ownerMap = {}
        for (const m of hm || []) { if (!ownerMap[m.home_id]) ownerMap[m.home_id] = m.user_id }
        const profileMap = {}
        for (const p of profiles || []) profileMap[p.id] = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || '—'
        const appCountMap = {}
        for (const a of apps || []) appCountMap[a.home_id] = (appCountMap[a.home_id] || 0) + 1
        columns = ['Address', 'City', 'Owner', 'Appliances', 'Created']
        rows = (homes || []).map(h => ({
          address: h.address || h.street_address || h.full_address || '—',
          city: h.city || '—',
          owner: profileMap[ownerMap[h.id]] || '—',
          appliances: appCountMap[h.id] || 0,
          created: new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        }))
      }

      if (type === 'appliances') {
        const [apps, { data: homes }] = await Promise.all([
          fetchAllActivePlacements(supabase),
          supabase.from('homes').select('id, address, city, street_address'),
        ])
        const homeMap = {}
        for (const h of homes || []) homeMap[h.id] = h.address || h.street_address || h.city || '—'
        columns = ['Name', 'Make', 'Model', 'Home', 'Has Manual', 'Created']
        rows = (apps || []).map(a => ({
          name: a.name || '—',
          make: a.make || '—',
          model: a.model || '—',
          home: homeMap[a.home_id] || '—',
          has_manual: a.manual_url && !a.manual_url.includes('google.com') ? 'Yes ✓' : 'No',
          created: new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        }))
      }

      if (type === 'systems') {
        const [{ data: systems }, { data: homes }] = await Promise.all([
          supabase.from('systems').select('*').order('created_at', { ascending: false }),
          supabase.from('homes').select('id, address, city, street_address'),
        ])
        const homeMap = {}
        for (const h of homes || []) homeMap[h.id] = h.address || h.street_address || h.city || '—'
        columns = ['Name', 'Type', 'Home', 'Status']
        rows = (systems || []).map(s => ({
          name: s.name || '—',
          type: s.type || s.system_type || '—',
          home: homeMap[s.home_id] || '—',
          status: s.status || '—',
        }))
      }

      if (type === 'manuals') {
        const [{ data: lookups }, allApps, { data: homes }] = await Promise.all([
          supabase.from('manual_lookups').select('*').order('created_at', { ascending: false }),
          fetchAllActivePlacements(supabase),
          supabase.from('homes').select('id, address, city, street_address'),
        ])
        const apps = (allApps || []).filter(a => a.manual_url && !a.manual_url.includes('google.com'))
        const homeMap = {}
        for (const h of homes || []) homeMap[h.id] = h.address || h.street_address || h.city || '—'
        const totalLookups = lookups?.length || 0
        const foundCount = lookups?.filter(l => l.result === 'found').length || 0
        const estCost = (totalLookups * 0.002).toFixed(3)
        note = `Total lookups: ${totalLookups} · Found: ${foundCount} · Not found: ${totalLookups - foundCount} · Est. cost: $${estCost} (avg ~$0.002/lookup)`

        if (lookups && lookups.length > 0) {
          columns = ['Date', 'Make', 'Model', 'Result', 'Confidence', 'Manual URL']
          rows = lookups.map(l => ({
            date: new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            make: l.make || '—',
            model: l.model || '—',
            result: l.result === 'found' ? 'Found ✓' : 'Not found',
            confidence: l.confidence || '—',
            manual_url: l.manual_url || '—',
          }))
        } else {
          columns = ['Appliance', 'Make', 'Model', 'Home', 'Manual URL']
          rows = (apps || []).map(a => ({
            appliance: a.name || '—',
            make: a.make || '—',
            model: a.model || '—',
            home: homeMap[a.home_id] || '—',
            manual_url: a.manual_url || '—',
          }))
        }
      }

      if (type === 'logs') {
        const { data: logs } = await supabase.from('maintenance_logs').select('*').order('created_at', { ascending: false }).limit(100)
        const assetIds = [...new Set((logs || []).map(l => l.asset_id).filter(Boolean))]
        const { data: assets } = assetIds.length
          ? await supabase.from('appliance_assets').select('id, type, make, model').in('id', assetIds)
          : { data: [] }
        const appMap = {}
        for (const a of assets || []) {
          const name = NAME_BY_TYPE[a.type] || a.type || 'Appliance'
          appMap[a.id] = [name, a.make, a.model].filter(Boolean).join(' ').trim() || '—'
        }
        columns = ['Task', 'Appliance', 'Completed', 'Cost']
        rows = (logs || []).map(l => ({
          task: l.task_name || l.title || l.description || '—',
          appliance: appMap[l.asset_id] || '—',
          completed: new Date(l.completed_at || l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          cost: l.cost ? `$${l.cost}` : '—',
        }))
      }

      if (type === 'avgappliances') {
        const [{ data: homes }, apps, { data: hm }, { data: profiles }] = await Promise.all([
          supabase.from('homes').select('*').order('created_at', { ascending: false }),
          fetchAllActivePlacements(supabase),
          supabase.from('home_members').select('home_id, user_id'),
          supabase.from('profiles').select('id, full_name, first_name, last_name'),
        ])
        const appCountMap = {}
        for (const a of apps || []) appCountMap[a.home_id] = (appCountMap[a.home_id] || 0) + 1
        const ownerMap = {}
        for (const m of hm || []) { if (!ownerMap[m.home_id]) ownerMap[m.home_id] = m.user_id }
        const profileMap = {}
        for (const p of profiles || []) profileMap[p.id] = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || '—'
        columns = ['Home', 'Owner', 'Appliances']
        rows = (homes || []).map(h => ({
          home: h.address || h.street_address || h.city || '—',
          owner: profileMap[ownerMap[h.id]] || '—',
          appliances: appCountMap[h.id] || 0,
        }))
      }

      setDrilldown({ title: titles[type] || type, loading: false, columns, rows, note })
    } catch (err) {
      console.error('Drilldown error:', err)
      setDrilldown(prev => ({ ...prev, loading: false, columns: ['Error'], rows: [{ error: err.message }] }))
    }
  }

  async function toggleTask(task) {
    const newStatus = task.status === 'done' ? 'not_started' : 'done'
    await supabase.from('owner_tasks').update({ status: newStatus }).eq('id', task.id)
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t))
  }

  async function addTask() {
    if (!newTask.task.trim()) return
    const { data } = await supabase.from('owner_tasks')
      .insert({ task: newTask.task, category: newTask.category, status: 'not_started' }).select().single()
    if (data) {
      setTasks(prev => [...prev, data])
      setNewTask({ task: '', category: 'Product' })
      setShowAddTask(false)
    }
  }

  async function deleteTask(id) {
    await supabase.from('owner_tasks').delete().eq('id', id)
    setTasks(prev => prev.filter(t => t.id !== id))
  }

  async function updateConfig(key, value) {
    await supabase.from('owner_config').update({ value }).eq('key', key)
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  async function toggleLaunchItem(index) {
    const list = [...(config.launch_checklist || [])]
    list[index] = { ...list[index], done: !list[index].done }
    await updateConfig('launch_checklist', list)
  }

  async function getOwnerHome() {
    const { data } = await supabase.from('homes').select('id, address, city, state').eq('created_by', OWNER_ID).single()
    return data
  }

  async function addBetaUser() {
    if (!newBetaUser.name.trim() || !newBetaUser.email.trim()) {
      alert('Name and email are required')
      return
    }
    const addedName = newBetaUser.name.trim()
    const addedEmail = newBetaUser.email.trim()
    try {
      const { data, error } = await supabase.from('beta_users').insert({
        name: addedName,
        email: addedEmail,
        status: newBetaUser.status || 'invited',
        source: newBetaUser.source || null,
        nps_score: newBetaUser.nps_score !== '' ? parseInt(newBetaUser.nps_score) : null,
        notes: newBetaUser.notes || null,
        referred_by: newBetaUser.referred_by || null,
        referred_by_user_id: newBetaUser.referred_by_user_id || null,
      }).select().single()
      if (error) throw error

      // Generate invite token client-side — guaranteed regardless of edge function
      const token = crypto.randomUUID().replace(/-/g, '')
      const homeData = await getOwnerHome()
      const inviteUrl = `https://casavio-app.vercel.app/invite/${token}`

      await supabase.from('invites').insert({
        home_id: homeData?.id,
        invited_email: addedEmail,
        relation: 'guest',
        role: 'viewer',
        token,
        status: 'pending',
        home_address: homeData ? `${homeData.address}, ${homeData.city}, ${homeData.state}` : 'Casavio',
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      })

      await supabase.from('beta_users').update({ invite_url: inviteUrl }).eq('id', data.id)

      setNewBetaUser({ name: '', email: '', status: 'invited', source: '', nps_score: '', notes: '', referred_by: null, referred_by_user_id: null })
      setShowAddBeta(false)
      setBetaUsers(prev => [{ ...data, invite_url: inviteUrl }, ...prev])

      // Best-effort email via edge function
      supabase.functions.invoke('send-invite', {
        body: {
          home_id: homeData?.id,
          invited_email: addedEmail,
          relation: 'guest',
          role: 'viewer',
          invited_by_name: 'Kenneth at Casavio',
          home_address: homeData ? `${homeData.address}, ${homeData.city}, ${homeData.state}` : 'Casavio',
        },
      }).then(({ data: inviteData }) => {
        if (inviteData?.email_sent) {
          alert(`${addedName} added and invite email sent to ${addedEmail}!`)
        }
      }).catch(() => {})
    } catch (err) {
      console.error('Failed to add beta user:', err)
      alert('Failed to add: ' + err.message)
    }
  }

  async function saveBetaUser(id) {
    const { error } = await supabase.from('beta_users').update({
      name: editBetaUserData.name,
      email: editBetaUserData.email,
      status: editBetaUserData.status,
      source: editBetaUserData.source || null,
      nps_score: editBetaUserData.nps_score !== '' ? parseInt(editBetaUserData.nps_score) : null,
      notes: editBetaUserData.notes || null,
    }).eq('id', id)
    if (error) { alert('Failed to save: ' + error.message); return }
    setBetaUsers(prev => prev.map(u => u.id === id ? { ...u, ...editBetaUserData, nps_score: editBetaUserData.nps_score !== '' ? parseInt(editBetaUserData.nps_score) : null } : u))
    setEditingBetaUser(null)
  }

  async function deleteBetaUser(id) {
    if (!window.confirm('Remove this beta user?')) return
    const { error } = await supabase.from('beta_users').delete().eq('id', id)
    if (error) { alert('Failed: ' + error.message); return }
    setBetaUsers(prev => prev.filter(u => u.id !== id))
  }

  async function resendInvite(user) {
    const homeData = await getOwnerHome()
    const { data: inviteData, error } = await supabase.functions.invoke('send-invite', {
      body: {
        home_id: homeData?.id,
        invited_email: user.email,
        relation: 'guest',
        role: 'viewer',
        invited_by_name: 'Kenneth at Casavio',
        home_address: homeData ? `${homeData.address}, ${homeData.city}, ${homeData.state}` : 'Casavio',
      },
    })
    console.log('Full invite response:', inviteData)
    console.log('email_sent:', inviteData?.email_sent, 'invite_url:', inviteData?.invite_url)

    if (inviteData?.invite_url) {
      await supabase.from('beta_users').update({ invite_url: inviteData.invite_url }).eq('id', user.id)
      setBetaUsers(prev => prev.map(u => u.id === user.id ? { ...u, invite_url: inviteData.invite_url } : u))
    }

    if (error) {
      alert('Failed to send: ' + error.message)
    } else if (!inviteData?.email_sent) {
      // link saved to row — no alert needed
    } else {
      alert(`Invite sent to ${user.email}!`)
    }
  }

  async function addFeature() {
    if (!newFeature.title.trim()) return
    const { data } = await supabase.from('feature_requests')
      .insert({ ...newFeature, status: 'backlog', votes: 0, user_id: OWNER_ID }).select().single()
    if (data) {
      setFeatures(prev => [data, ...prev])
      setNewFeature({ title: '', description: '' })
      setShowAddFeature(false)
    }
  }

  async function moveFeature(feat, newStatus) {
    await supabase.from('feature_requests').update({ status: newStatus }).eq('id', feat.id)
    setFeatures(prev => prev.map(fr => fr.id === feat.id ? { ...fr, status: newStatus } : fr))
  }

  async function upvoteFeature(feat) {
    const votes = (feat.votes || 0) + 1
    await supabase.from('feature_requests').update({ votes }).eq('id', feat.id)
    setFeatures(prev => prev.map(fr => fr.id === feat.id ? { ...fr, votes } : fr))
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, fontFamily: f, fontSize: '20px', color: C.amber }}>
      Loading Command Center…
    </div>
  )

  const tasksByCategory = {}
  for (const t of tasks) {
    if (!tasksByCategory[t.category]) tasksByCategory[t.category] = []
    tasksByCategory[t.category].push(t)
  }

  const safeArr = v => Array.isArray(v) ? v : []
  const techStack = safeArr(config.tech_stack)
  const launchChecklist = safeArr(config.launch_checklist)
  const revenue = config.revenue || {}
  const competitors = safeArr(config.competitors)
  const advisors = safeArr(config.advisors)
  const mrrData = safeArr(revenue.monthly_data)

  const techTotal = techStack.reduce((sum, item) => sum + (item.cost_monthly || 0), 0)

  const newsCategoryEmojis = (() => {
    const map = {
      'PROPTECH & HOME MANAGEMENT': '🏠', 'REAL ESTATE — NORTH BAY CA': '📍',
      'HOME SERVICES & CONTRACTORS': '🔧', 'SAAS FOUNDER & STARTUP': '🚀',
      'AI TOOLS & ANTHROPIC': '🤖', 'PROPTECH': '🏠', 'REAL ESTATE': '📍',
      'HOME SERVICES': '🔧', 'SAAS FOUNDER': '🚀', 'AI TOOLS': '🤖',
    }
    for (const t of (config.news_settings?.topics || [])) if (t?.label) map[t.label.toUpperCase()] = t.emoji
    return map
  })()

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: fs }}>

      {/* Quick Launch Bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: C.dark, borderBottom: `2px solid ${C.amber}`,
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '0 20px', height: '50px',
      }}>
        <CasavioLogo size={15} color={C.amber} style={{ marginRight: '12px', flexShrink: 0 }} />
        {[
          { label: '← App', href: '/', external: false },
          { label: '💬 Beta Community', href: '/beta', external: false },
          { label: '🏷️ Data Plates', href: '/owner/data-plates', external: false },
          { label: '🧪 AI Test Bench', href: '/owner/ai-test-bench', external: false },
          { label: 'Supabase', href: 'https://supabase.com/dashboard/project/maajaepibcnbxcqjahej', external: true },
          { label: 'Vercel', href: 'https://vercel.com/dashboard', external: true },
          { label: 'GitHub', href: 'https://github.com', external: true },
          { label: 'Stripe', href: 'https://dashboard.stripe.com', external: true },
        ].map(link => (
          <a
            key={link.label}
            href={link.href}
            target={link.external ? '_blank' : '_self'}
            rel="noreferrer"
            style={{
              color: '#CCC', textDecoration: 'none', fontSize: '12px',
              padding: '3px 10px', borderRadius: '6px',
              background: '#2A1E12', border: '1px solid #3D2C1A', fontFamily: fs,
              whiteSpace: 'nowrap',
            }}
          >
            {link.label}
          </a>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ color: C.muted, fontSize: '11px', fontFamily: fs, marginRight: '8px' }}>Kenneth · Owner</span>
        <button
          onClick={() => supabase.auth.signOut()}
          style={{ background: C.danger, color: 'white', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: fs }}
        >
          Sign Out
        </button>
      </div>

      {/* Page body — replaced by OwnerSandbox when the AI Calibration
          Sandbox is open. Quick Launch Bar above stays visible either way. */}
      {showSandbox ? (
        <OwnerSandbox session={session} onBack={() => setShowSandbox(false)} />
      ) : (
      <div style={{ maxWidth: '980px', margin: '0 auto', padding: '28px 20px' }}>
        <h1 style={{ fontFamily: f, fontSize: '26px', color: C.brown, margin: '0 0 4px' }}>Founder Command Center</h1>
        <p style={{ color: C.muted, fontSize: '12px', margin: '0 0 28px', fontFamily: fs }}>Private — Kenneth only</p>

        {/* Dev & Ops — project-specific external dashboards. Distinct from
            the generic platform links in the top Quick Launch Bar; these
            are deep links into the casavio-app project on each service. */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', marginBottom: '20px', padding: '16px 20px 18px' }}>
          <h2 style={{ margin: '0 0 12px', fontFamily: f, fontSize: '15px', color: C.brown }}>Dev & Ops</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              { label: '🤖 Anthropic Console', href: 'https://console.anthropic.com/' },
              { label: '🗄️ Supabase Dashboard', href: 'https://supabase.com/dashboard/project/maajaepibcnbxcqjahej' },
              { label: '▲ Vercel Deployments', href: 'https://vercel.com/casavio/casavio-app/deployments' },
              { label: '🐙 GitHub Repo', href: 'https://github.com/kennethkrummes/casavio-app' },
            ].map(link => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: C.brown, textDecoration: 'none', fontSize: '13px',
                  padding: '8px 14px', borderRadius: '8px',
                  background: C.bg, border: `1px solid ${C.border}`, fontFamily: fs,
                  whiteSpace: 'nowrap',
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Users — registered accounts, read-only (v1: no delete/edit). First
            thing after the dev links so new signups are immediately visible. */}
        <UsersTile />

        {/* External Services — third-party vendors Casavio uses or will
            use. Active vendors link directly to dashboards. Pending vendors
            link to signup but reveal context first (tap-to-reveal-then-tap-
            Continue) so accidental taps don't drop the user into a signup
            flow they aren't ready for. */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', marginBottom: '20px', padding: '16px 20px 18px' }}>
          <h2 style={{ margin: '0 0 12px', fontFamily: f, fontSize: '15px', color: C.brown }}>External Services</h2>

          {/* Active */}
          <div style={{ fontFamily: fs, fontSize: '10px', fontWeight: '700', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', margin: '4px 0 8px' }}>
            Active
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              { label: '🛒 Amazon Associates', href: 'https://affiliate-program.amazon.com/home', title: 'Affiliate ID: casavio-20' },
              { label: '✉️ Resend', href: 'https://resend.com/emails', title: 'Transactional email — invites, notifications' },
              { label: '☁️ Google Cloud Console', href: 'https://console.cloud.google.com/', title: 'Google Places API key + Vision API' },
            ].map(link => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                title={link.title}
                style={{
                  color: C.brown, textDecoration: 'none', fontSize: '13px',
                  padding: '8px 14px', borderRadius: '8px',
                  background: C.bg, border: `1px solid ${C.border}`, fontFamily: fs,
                  whiteSpace: 'nowrap',
                }}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Pending Setup */}
          <div style={{ fontFamily: fs, fontSize: '10px', fontWeight: '700', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', margin: '18px 0 8px' }}>
            Pending Setup
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              {
                id: 'stripe', label: '💳 Stripe', name: 'Stripe',
                href: 'https://dashboard.stripe.com/register',
                context: 'Required for paid subscriptions and Phase 2 mail-printed-copy service. Setup blocked on California LLC formation (Stripe requires a registered business). Cost: $0 setup, 2.9% + $0.30 per transaction.',
              },
              {
                id: 'apple', label: '🍎 Apple Developer', name: 'Apple Developer Program',
                href: 'https://developer.apple.com/programs/enroll/',
                context: "Required if Casavio ships a native iOS app for LiDAR room scanning (Phase 3 roadmap). $99/year. Not needed for current PWA. Don't enroll until LiDAR build is greenlit.",
              },
              {
                id: 'domain', label: '🌐 Domain Registrar', name: 'Cloudflare Registrar',
                href: 'https://www.cloudflare.com/products/registrar/',
                context: 'For purchasing casavio.com (or .app). Recommended: Cloudflare Registrar (cost-plus pricing, no markup). Alternates: Porkbun, Namecheap. Needed before Resend domain verification and Stripe production. Cost: ~$10-15/year for .com.',
              },
              {
                id: 'plausible', label: '📈 Plausible Analytics', name: 'Plausible',
                href: 'https://plausible.io/register',
                context: 'Optional. Plausible is privacy-focused, no cookies, ~$9/month. Alternative: Google Analytics (free, cookie-based, more invasive). Defer until Casavio has external users to track.',
              },
              {
                id: 'twitter', label: '𝕏 X (Twitter)', name: 'X (Twitter)',
                href: 'https://twitter.com/i/flow/signup',
                context: "Casavio's social presence. Set up when ready for public marketing. Defer until first paying user or beta launch announcement.",
              },
            ].map(vendor => {
              const isExpanded = expandedPendingVendor === vendor.id
              return (
                <button
                  key={vendor.id}
                  onClick={() => setExpandedPendingVendor(isExpanded ? null : vendor.id)}
                  title={vendor.context}
                  style={{
                    color: isExpanded ? C.brown : C.muted,
                    fontSize: '13px',
                    padding: '8px 14px', borderRadius: '8px',
                    background: C.bg,
                    border: `1px dashed ${C.border}`,
                    fontFamily: fs,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    fontWeight: isExpanded ? '700' : '400',
                  }}
                >
                  {vendor.label}
                </button>
              )
            })}
          </div>

          {expandedPendingVendor && (() => {
            const PENDING_BY_ID = {
              stripe: { name: 'Stripe', href: 'https://dashboard.stripe.com/register', context: 'Required for paid subscriptions and Phase 2 mail-printed-copy service. Setup blocked on California LLC formation (Stripe requires a registered business). Cost: $0 setup, 2.9% + $0.30 per transaction.' },
              apple: { name: 'Apple Developer Program', href: 'https://developer.apple.com/programs/enroll/', context: "Required if Casavio ships a native iOS app for LiDAR room scanning (Phase 3 roadmap). $99/year. Not needed for current PWA. Don't enroll until LiDAR build is greenlit." },
              domain: { name: 'Cloudflare Registrar', href: 'https://www.cloudflare.com/products/registrar/', context: 'For purchasing casavio.com (or .app). Recommended: Cloudflare Registrar (cost-plus pricing, no markup). Alternates: Porkbun, Namecheap. Needed before Resend domain verification and Stripe production. Cost: ~$10-15/year for .com.' },
              plausible: { name: 'Plausible', href: 'https://plausible.io/register', context: 'Optional. Plausible is privacy-focused, no cookies, ~$9/month. Alternative: Google Analytics (free, cookie-based, more invasive). Defer until Casavio has external users to track.' },
              twitter: { name: 'X (Twitter)', href: 'https://twitter.com/i/flow/signup', context: "Casavio's social presence. Set up when ready for public marketing. Defer until first paying user or beta launch announcement." },
            }
            const v = PENDING_BY_ID[expandedPendingVendor]
            if (!v) return null
            return (
              <div style={{ marginTop: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <div style={{ fontFamily: f, fontSize: '14px', fontWeight: '700', color: C.brown }}>{v.name}</div>
                  <button
                    onClick={() => setExpandedPendingVendor(null)}
                    aria-label="Close"
                    style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ fontFamily: fs, fontSize: '12px', color: C.brown, lineHeight: 1.55, marginBottom: 12 }}>
                  {v.context}
                </div>
                <a
                  href={v.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    background: C.amber, color: '#fff',
                    padding: '8px 16px', borderRadius: 8,
                    fontFamily: fs, fontSize: 13, fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  Continue to {v.name} signup →
                </a>
              </div>
            )
          })()}
        </div>

        {/* AI Calibration Sandbox — owner-only workshop. Tile opens an
            in-page sub-view (not a separate route). Ken drops a photo,
            Casavio runs the production detection pipeline, Ken corrects
            what's wrong. Corrections write to ai_corrections (source=
            'sandbox') and seed prompt iteration over time. */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', marginBottom: '20px', padding: '16px 20px 18px' }}>
          <h2 style={{ margin: '0 0 8px', fontFamily: f, fontSize: '15px', color: C.brown }}>🧠 AI Calibration Sandbox</h2>
          <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, lineHeight: 1.55, marginBottom: 12 }}>
            Drop a photo from an advisor, hardware store visit, or open house. See what Casavio's detection pipeline produces, then correct what's wrong. Corrections feed the calibration dataset that drives prompt iteration.
          </div>
          <button
            onClick={() => setShowSandbox(true)}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9' }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
            style={{
              background: C.amber,
              color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 18px', fontFamily: f, fontSize: 14,
              fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
              transition: 'opacity 120ms ease-out',
            }}
          >
            Open Sandbox →
          </button>
        </div>

        {/* Launch Claude Code (PowerShell) */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', marginBottom: '20px', padding: '16px 20px 20px' }}>
          <h2 style={{ margin: '0 0 14px', fontFamily: f, fontSize: '15px', color: C.brown }}>Launch Claude Code (PowerShell)</h2>

          {[
            {
              id: 'cmd-cd',
              label: 'Step 1: Navigate to the project',
              content: 'cd "C:\\Users\\kenne\\OneDrive\\Desktop\\casavio\\casavio-app"',
              note: 'Quotes are required because of the space in the path. After pasting, confirm the prompt changes to ...casavio-app>',
            },
            {
              id: 'cmd-claude',
              label: 'Step 2: Start Claude Code',
              content: 'claude',
              note: "Paste only after Step 1's prompt has changed.",
            },
          ].map(block => (
            <div key={block.id} style={{ marginBottom: '14px' }}>
              <div style={{ fontFamily: fs, fontSize: '12px', fontWeight: '600', color: C.brown, marginBottom: '6px' }}>{block.label}</div>
              <div style={{ position: 'relative', background: C.dark, borderRadius: '8px', padding: '12px 56px 12px 14px' }}>
                <code style={{
                  display: 'block',
                  fontFamily: 'Consolas, "Courier New", monospace',
                  fontSize: '13px',
                  color: '#F2EDE4',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  lineHeight: '1.5',
                }}>{block.content}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(block.content); setCopiedId(block.id); setTimeout(() => setCopiedId(null), 2000) }}
                  title="Copy to clipboard"
                  style={{
                    position: 'absolute', top: '8px', right: '8px',
                    background: copiedId === block.id ? '#16A34A' : C.amber,
                    color: '#fff', border: 'none', borderRadius: '6px',
                    padding: '4px 8px', cursor: 'pointer',
                    fontSize: '11px', fontFamily: fs, fontWeight: '600',
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  {copiedId === block.id ? (
                    '✓ Copied'
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '6px', lineHeight: '1.4' }}>{block.note}</div>
            </div>
          ))}

          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '6px', paddingTop: '14px' }}>
            <div style={{ fontFamily: f, fontSize: '13px', color: C.brown, marginBottom: '10px' }}>Useful Links</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
              {[
                { title: 'Anthropic Console', href: 'https://console.anthropic.com', desc: 'Manage API keys, view usage' },
                { title: 'Supabase Dashboard', href: 'https://supabase.com/dashboard/project/maajaepibcnbxcqjahej', desc: 'Database, Edge Functions, Logs' },
                { title: 'Vercel Deployments', href: 'https://vercel.com/dashboard', desc: 'Build status, deploy logs' },
                { title: 'GitHub Repo', href: 'https://github.com/kennethkrummes/casavio-app', desc: 'Source code, commits' },
              ].map(tile => (
                <a
                  key={tile.title}
                  href={tile.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block', textDecoration: 'none',
                    background: C.bg, border: `1px solid ${C.border}`,
                    borderRadius: '8px', padding: '10px 12px',
                  }}
                >
                  <div style={{ fontFamily: f, fontSize: '13px', color: C.amber, fontWeight: '600' }}>{tile.title} ↗</div>
                  <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '3px' }}>{tile.desc}</div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Industry News Feed */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', marginBottom: '20px', padding: '16px 20px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontFamily: f, fontSize: '15px', color: C.brown }}>📰 Industry News</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {!newsLoading && news.length > 0 && (
                <span style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>{news.length} stories</span>
              )}
              <button
                onClick={fetchNews}
                disabled={newsLoading}
                style={{ background: 'none', border: 'none', color: newsLoading ? C.muted : C.amber, cursor: newsLoading ? 'default' : 'pointer', fontSize: '13px', fontFamily: fs, padding: 0 }}
              >
                {newsLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>
          </div>
          {newsLoading ? (
            <div style={{ color: C.muted, fontSize: '13px', fontFamily: fs, padding: '8px 0' }}>Finding today's top stories…</div>
          ) : news.length === 0 ? (
            <div style={{ color: C.muted, fontSize: '13px', fontFamily: fs }}>No stories loaded yet. Click Refresh to fetch the latest news.</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '16px',
            }}>
              {news.map((article, i) => (
                <div key={i} style={{
                  background: C.bg, borderRadius: '10px', padding: '16px',
                  borderTop: `3px solid ${C.amber}`,
                  display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', color: C.muted, marginBottom: '8px', fontFamily: fs }}>
                    {newsCategoryEmojis[article.category] || '📰'} {article.category}
                  </div>
                  <a href={article.url} target="_blank" rel="noopener noreferrer" style={{
                    fontFamily: f, fontSize: '14px', color: C.brown,
                    textDecoration: 'none', fontWeight: 'bold',
                    display: 'block', marginBottom: '8px', lineHeight: '1.4',
                  }}>
                    {article.headline}
                  </a>
                  <div style={{ fontSize: '11px', color: C.muted, marginBottom: '8px', fontFamily: fs }}>
                    {article.source} · {article.published}
                  </div>
                  <div style={{ fontSize: '12px', color: '#4A3728', lineHeight: '1.5', fontFamily: fs }}>
                    {article.summary}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* News Settings */}
        <SectionCard title="⚙️ News Settings" defaultOpen={false}>
          {!newsSettings ? (
            <div style={{ fontSize: '12px', color: C.muted }}>Loading settings…</div>
          ) : (
            <>
              {/* Email Controls */}
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px', fontFamily: fs }}>Email Controls</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: C.brown, fontFamily: fs, cursor: 'pointer', marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    checked={newsSettings.email_enabled}
                    onChange={e => setNewsSettings(p => ({ ...p, email_enabled: e.target.checked }))}
                    style={{ accentColor: C.success, width: '15px', height: '15px' }}
                  />
                  Email briefing enabled
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: C.muted, fontFamily: fs, minWidth: '68px' }}>Frequency</span>
                  <select value={newsSettings.email_frequency} onChange={e => setNewsSettings(p => ({ ...p, email_frequency: e.target.value }))} style={selectStyle}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly (Monday)</option>
                    <option value="weekdays">Weekdays only</option>
                  </select>
                  <span style={{ fontSize: '12px', color: C.muted, fontFamily: fs }}>at</span>
                  <input type="time" value={newsSettings.email_time} onChange={e => setNewsSettings(p => ({ ...p, email_time: e.target.value }))} style={{ ...selectStyle, minWidth: '100px' }} />
                  <span style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>Pacific</span>
                </div>
                <button onClick={sendTestEmail} disabled={testEmailSending} style={btnStyle(testEmailSending ? C.muted : C.amber)}>
                  {testEmailSending ? 'Sending…' : '📧 Send test email now'}
                </button>
                {testEmailResult && (
                  <div style={{
                    marginTop: '8px', fontSize: '12px', fontFamily: fs,
                    color: testEmailResult.ok ? C.success : C.danger,
                  }}>
                    {testEmailResult.ok ? '✓' : '✕'} {testEmailResult.message}
                  </div>
                )}
              </div>

              {/* Topic Controls */}
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px', fontFamily: fs }}>Topics</div>
                {(newsSettings.topics || []).map((topic, i) => (
                  <div key={topic.id || i} style={{ padding: '10px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}`, marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '15px' }}>{topic.emoji}</span>
                      <span style={{ flex: 1, fontSize: '13px', color: C.brown, fontFamily: fs }}>{topic.label}</span>
                      <input
                        type="checkbox"
                        checked={topic.enabled}
                        onChange={e => {
                          const updated = [...newsSettings.topics]
                          updated[i] = { ...updated[i], enabled: e.target.checked }
                          setNewsSettings(p => ({ ...p, topics: updated }))
                        }}
                        style={{ accentColor: C.success, width: '14px', height: '14px', cursor: 'pointer' }}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Custom search terms (optional — overrides default query)"
                      value={topic.custom_query || ''}
                      onChange={e => {
                        const updated = [...newsSettings.topics]
                        updated[i] = { ...updated[i], custom_query: e.target.value }
                        setNewsSettings(p => ({ ...p, topics: updated }))
                      }}
                      style={{ ...inputStyle, width: '100%', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
                <button
                  onClick={() => setNewsSettings(p => ({
                    ...p,
                    topics: [...p.topics, { id: `custom_${Date.now()}`, label: 'New Topic', emoji: '📰', enabled: true, custom_query: '' }],
                  }))}
                  style={{ ...btnStyle(C.muted), marginTop: '4px' }}
                >
                  + Add custom topic
                </button>
              </div>

              {/* Feed Controls */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '10px', fontFamily: fs }}>Feed Controls</div>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, marginBottom: '4px' }}>Articles per topic</div>
                    <select value={newsSettings.articles_per_topic} onChange={e => setNewsSettings(p => ({ ...p, articles_per_topic: Number(e.target.value) }))} style={selectStyle}>
                      {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, marginBottom: '4px' }}>Max article age</div>
                    <select value={newsSettings.max_age_days} onChange={e => setNewsSettings(p => ({ ...p, max_age_days: Number(e.target.value) }))} style={selectStyle}>
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={365}>Any time</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, marginBottom: '4px' }}>Cache duration</div>
                    <select value={newsSettings.cache_hours || 4} onChange={e => setNewsSettings(p => ({ ...p, cache_hours: Number(e.target.value) }))} style={selectStyle}>
                      <option value={1}>1 hour</option>
                      <option value={4}>4 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours</option>
                    </select>
                  </div>
                </div>
              </div>

              <button onClick={saveNewsSettings} style={{ ...btnStyle(newsSaved ? C.success : C.amber), minWidth: '110px' }}>
                {newsSaved ? 'Saved ✓' : 'Save Settings'}
              </button>
            </>
          )}
        </SectionCard>

        {/* 1. App Metrics */}
        <SectionCard title="📊 App Metrics">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <StatTile label="👤 Total Users" value={metrics.users} onClick={() => openDrilldown('users')} />
            <StatTile label="🏠 Properties" value={metrics.homes} onClick={() => openDrilldown('homes')} />
            <StatTile label="📦 Appliances" value={metrics.appliances} onClick={() => openDrilldown('appliances')} />
            <StatTile label="⚙️ Home Systems" value={metrics.systems} onClick={() => openDrilldown('systems')} />
            <StatTile label="📖 Manuals Found" value={metrics.manualsFound} onClick={() => openDrilldown('manuals')} />
            <StatTile label="✅ Tasks Completed" value={metrics.tasksCompleted} onClick={() => openDrilldown('logs')} />
            <StatTile label="🆕 New This Month" value={metrics.newThisMonth} onClick={() => openDrilldown('newthismonth')} />
            <StatTile label="📊 Avg Appl/Home" value={metrics.homes ? (metrics.appliances / metrics.homes).toFixed(1) : '—'} onClick={() => openDrilldown('avgappliances')} />
            <StatTile label="🔨 Pending Requests" value={metrics.pendingRequests ?? 0} />
            <StatTile label="📋 Active Jobs" value={metrics.totalJobs} />
            <StatTile label="🔴 Emergency Jobs" value={metrics.emergencyJobs} />
            <StatTile label="⭐ Wishlist Jobs" value={metrics.wishlistJobs} />
          </div>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '8px' }}>Signups by Month (last 6)</div>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={metrics.signupsByMonth} margin={{ top: 0, right: 0, bottom: 0, left: -22 }}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: fs }} />
              <YAxis tick={{ fontSize: 10, fontFamily: fs }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontFamily: fs, fontSize: '12px' }} />
              <Bar dataKey="signups" fill={C.amber} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        {/* 2. Revenue & Runway */}
        <SectionCard title="💰 Revenue & Runway">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <StatTile label="MRR" value={`$${(revenue.mrr || 0).toLocaleString()}`} />
            <StatTile label="ARR" value={`$${(revenue.arr || 0).toLocaleString()}`} />
            <StatTile label="Monthly Burn" value={`$${(revenue.burn_rate || 0).toLocaleString()}`} />
            <StatTile label="Runway" value={`${revenue.runway_months || 0}mo`} />
          </div>
          {mrrData.length > 0 ? (
            <>
              <div style={{ fontSize: '12px', color: C.muted, marginBottom: '8px' }}>MRR Over Time</div>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={mrrData} margin={{ top: 0, right: 0, bottom: 0, left: -22 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: fs }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: fs }} />
                  <Tooltip contentStyle={{ fontFamily: fs, fontSize: '12px' }} />
                  <Line type="monotone" dataKey="mrr" stroke={C.success} strokeWidth={2} dot={{ r: 4, fill: C.success }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div style={{ fontSize: '12px', color: C.muted }}>No MRR history yet — add monthly_data to revenue config.</div>
          )}
        </SectionCard>

        {/* 3. Business Checklist */}
        <SectionCard title="✅ Business Checklist">
          {TASK_CATEGORIES.map(cat => {
            const catTasks = tasksByCategory[cat] || []
            if (!catTasks.length) return null
            const doneCount = catTasks.filter(t => t.status === 'done').length
            return (
              <div key={cat} style={{ marginBottom: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontFamily: f, fontSize: '13px', fontWeight: '700', color: C.brown }}>{cat}</span>
                  <span style={{ fontSize: '11px', color: C.muted }}>({doneCount}/{catTasks.length})</span>
                  <div style={{ flex: 1, height: '3px', background: C.border, borderRadius: '2px', overflow: 'hidden', maxWidth: '80px' }}>
                    <div style={{ height: '100%', background: C.success, borderRadius: '2px', width: `${catTasks.length ? (doneCount / catTasks.length) * 100 : 0}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
                {catTasks.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 4px', borderBottom: `1px solid ${C.border}` }}>
                    <input
                      type="checkbox"
                      checked={t.status === 'done'}
                      onChange={() => toggleTask(t)}
                      style={{ accentColor: C.success, width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, fontSize: '13px', color: t.status === 'done' ? C.muted : C.brown, textDecoration: t.status === 'done' ? 'line-through' : 'none', fontFamily: fs }}>
                      {t.task}
                    </span>
                    {t.status === 'in_progress' && (
                      <span style={{ fontSize: '10px', color: C.blue, background: '#EEF4FF', border: '1px solid #2563EB44', borderRadius: '10px', padding: '2px 7px', fontFamily: fs, whiteSpace: 'nowrap' }}>In Progress</span>
                    )}
                    {t.notes && <span style={{ fontSize: '11px', color: C.muted, fontFamily: fs, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.notes}</span>}
                    <button
                      onClick={() => deleteTask(t.id)}
                      style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '15px', padding: '0 2px', lineHeight: 1 }}
                    >×</button>
                  </div>
                ))}
              </div>
            )
          })}
          {showAddTask ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px', padding: '12px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}` }}>
              <input
                value={newTask.task}
                onChange={e => setNewTask(p => ({ ...p, task: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addTask()}
                placeholder="Task title…"
                style={inputStyle}
                autoFocus
              />
              <select value={newTask.category} onChange={e => setNewTask(p => ({ ...p, category: e.target.value }))} style={selectStyle}>
                {TASK_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <button onClick={addTask} style={btnStyle(C.success)}>Add</button>
              <button onClick={() => setShowAddTask(false)} style={btnStyle(C.muted)}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowAddTask(true)} style={{ ...btnStyle(C.amber), marginTop: '10px' }}>+ Add Task</button>
          )}
        </SectionCard>

        {/* 4. Launch Checklist */}
        <SectionCard title="🚀 Launch Checklist">
          {launchChecklist.length === 0 && (
            <div style={{ fontSize: '12px', color: C.muted }}>No items yet.</div>
          )}
          {launchChecklist.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 4px', borderBottom: `1px solid ${C.border}` }}>
              <input
                type="checkbox"
                checked={item.done || false}
                onChange={() => toggleLaunchItem(i)}
                style={{ accentColor: C.success, width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ flex: 1, fontSize: '13px', color: item.done ? C.muted : C.brown, textDecoration: item.done ? 'line-through' : 'none', fontFamily: fs }}>
                {item.label || item.text}
              </span>
              {item.category && (
                <span style={{ fontSize: '10px', color: C.amber, background: '#FBF3E8', border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '2px 8px', fontFamily: fs, whiteSpace: 'nowrap' }}>
                  {item.category}
                </span>
              )}
            </div>
          ))}
          {launchChecklist.length > 0 && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: C.muted }}>
              {launchChecklist.filter(i => i.done).length}/{launchChecklist.length} complete
            </div>
          )}
        </SectionCard>

        {/* 5. Tech Stack & Costs */}
        <SectionCard title="🛠 Tech Stack & Costs" defaultOpen={false}>
          {techStack.length === 0 ? (
            <div style={{ fontSize: '12px', color: C.muted }}>No stack data.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: fs }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {['Tool', 'Plan', 'Cost/mo', 'Limit', 'Verify'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.muted, fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {techStack.map((item, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '7px 10px', fontWeight: '600' }}>
                          {item.url
                            ? <a href={item.url} target="_blank" rel="noreferrer" style={{ color: C.amber, textDecoration: 'none' }}>{item.name}</a>
                            : <span style={{ color: C.brown }}>{item.name}</span>}
                        </td>
                        <td style={{ padding: '7px 10px', color: C.muted }}>{item.plan}</td>
                        <td style={{ padding: '7px 10px', fontWeight: '600', color: item.cost_monthly === 0 ? C.success : C.brown }}>
                          {item.cost_monthly === 0 ? 'Free' : `$${item.cost_monthly}/mo`}
                        </td>
                        <td style={{ padding: '7px 10px', color: C.muted, fontSize: '12px' }}>{item.limit || '—'}</td>
                        <td style={{ padding: '7px 10px', fontSize: '11px' }}>
                          {item.last_verified && (
                            <div style={{ color: C.muted, fontSize: '10px', marginBottom: '2px' }}>
                              Last verified: {new Date(item.last_verified + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}
                          {item.pricing_url && (
                            <a href={item.pricing_url} target="_blank" rel="noreferrer"
                              style={{ color: C.amber, textDecoration: 'none', whiteSpace: 'nowrap', fontSize: '11px' }}>
                              Check pricing →
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '10px', fontSize: '12px', color: C.muted }}>
                Total stack cost: <strong style={{ color: C.brown }}>${techTotal.toFixed(2)}/mo</strong>
              </div>
            </>
          )}
        </SectionCard>

        {/* 6. Competitor Tracker */}
        <SectionCard title="🎯 Competitor Tracker" defaultOpen={false}>
          {competitors.length === 0 ? (
            <div style={{ fontSize: '12px', color: C.muted }}>No competitors tracked.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: fs }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                    {['Name', 'Pricing', 'Strength', 'Weakness', 'Notes'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.muted, fontWeight: '600', fontSize: '11px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((comp, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '7px 10px', fontWeight: '600' }}>
                        {comp.url
                          ? <a href={comp.url} target="_blank" rel="noreferrer" style={{ color: C.amber, textDecoration: 'none' }}>{comp.name}</a>
                          : <span style={{ color: C.brown }}>{comp.name}</span>}
                      </td>
                      <td style={{ padding: '7px 10px', color: C.muted }}>{comp.pricing}</td>
                      <td style={{ padding: '7px 10px', color: C.success, fontSize: '12px' }}>{comp.strength}</td>
                      <td style={{ padding: '7px 10px', color: C.danger, fontSize: '12px' }}>{comp.weakness}</td>
                      <td style={{ padding: '7px 10px', color: C.muted, fontSize: '12px' }}>{comp.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* 7. Beta User CRM */}
        <SectionCard title="👥 Beta User CRM">
          <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: fs }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                  {['Name', 'Email', 'Status', 'Source', 'NPS', 'Added', 'Notes', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.muted, fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {betaUsers.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: '12px 10px', color: C.muted, fontSize: '12px' }}>No beta users yet.</td></tr>
                )}
                {betaUsers.map(u => {
                  const isEditing = editingBetaUser === u.id
                  const ed = editBetaUserData
                  const inlineInput = (field, placeholder, type = 'text') => (
                    <input
                      type={type}
                      value={ed[field] ?? ''}
                      onChange={e => setEditBetaUserData(p => ({ ...p, [field]: e.target.value }))}
                      placeholder={placeholder}
                      style={{ ...inputStyle, minWidth: '80px', padding: '3px 6px', fontSize: '12px' }}
                    />
                  )
                  return (
                    <tr
                      key={u.id}
                      onClick={() => {
                        if (!isEditing) {
                          setEditingBetaUser(u.id)
                          setEditBetaUserData({ name: u.name || '', email: u.email || '', status: u.status || 'invited', source: u.source || '', nps_score: u.nps_score != null ? String(u.nps_score) : '', notes: u.notes || '' })
                        }
                      }}
                      style={{ borderBottom: `1px solid ${C.border}`, background: isEditing ? '#FFFBF5' : 'transparent', cursor: isEditing ? 'default' : 'pointer' }}
                      onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background = '#FFF8F0' }}
                      onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = 'transparent' }}
                    >
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? inlineInput('name', 'Name') : <span style={{ color: C.brown, fontWeight: '600', whiteSpace: 'nowrap' }}>{u.name || '—'}</span>}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? inlineInput('email', 'Email') : <span style={{ color: C.muted, fontSize: '12px' }}>{u.email}</span>}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? (
                          <select value={ed.status} onChange={e => setEditBetaUserData(p => ({ ...p, status: e.target.value }))} style={{ ...selectStyle, fontSize: '12px', padding: '3px 6px' }}>
                            {['invited', 'active', 'churned'].map(s => <option key={s}>{s}</option>)}
                          </select>
                        ) : (
                          <span style={{
                            fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', fontFamily: fs,
                            background: u.status === 'active' ? '#EBF5EF' : u.status === 'churned' ? '#F9EAE8' : '#FBF3E8',
                            color: u.status === 'active' ? C.success : u.status === 'churned' ? C.danger : C.amber,
                          }}>{u.status}</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? (
                          <select value={ed.source || ''} onChange={e => setEditBetaUserData(p => ({ ...p, source: e.target.value }))} style={{ ...selectStyle, fontSize: '12px', padding: '3px 6px' }}>
                            <option value="">—</option>
                            {['Personal Network', 'Advisor Referral', 'Social Media', 'Cold Outreach', 'Other'].map(s => <option key={s}>{s}</option>)}
                          </select>
                        ) : <span style={{ color: C.muted, fontSize: '12px', whiteSpace: 'nowrap' }}>{u.source || '—'}</span>}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? inlineInput('nps_score', '0–10', 'number') : (
                          <span style={{ color: u.nps_score != null ? C.brown : C.muted, fontSize: '12px', fontWeight: u.nps_score != null ? '600' : 'normal' }}>
                            {u.nps_score != null ? u.nps_score : '—'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px', color: C.muted, fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? inlineInput('notes', 'Notes…') : <span style={{ color: C.muted, fontSize: '12px', maxWidth: '180px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.notes || '—'}</span>}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? (
                          <>
                            <button onClick={e => { e.stopPropagation(); saveBetaUser(u.id) }} style={{ ...btnStyle(C.success), marginRight: '4px', padding: '3px 10px', fontSize: '11px' }}>Save</button>
                            <button onClick={e => { e.stopPropagation(); setEditingBetaUser(null) }} style={{ ...btnStyle(C.muted), padding: '3px 10px', fontSize: '11px' }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <div style={{ whiteSpace: 'nowrap' }}>
                              <button onClick={e => { e.stopPropagation(); resendInvite(u) }} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '12px', marginRight: '6px', fontFamily: fs }}>📧</button>
                              <button onClick={e => { e.stopPropagation(); deleteBetaUser(u.id) }} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '14px', fontFamily: fs }}>✕</button>
                            </div>
                            {u.invite_url && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }} onClick={e => e.stopPropagation()}>
                                <span style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>Invite link:</span>
                                <button
                                  onClick={e => {
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(u.invite_url)
                                    setCopiedId(u.id)
                                    setTimeout(() => setCopiedId(null), 2000)
                                  }}
                                  style={{ background: C.amber, color: '#fff', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                  {copiedId === u.id ? '✓ Copied!' : '📋 Copy Invite Link'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {betaUsers.length > 0 && (
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '8px', fontFamily: fs }}>Click any row to edit</div>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
            {!showAddBeta && (
              <button onClick={() => setShowAddBeta(true)} style={btnStyle(C.amber)}>+ Add Beta User</button>
            )}
          </div>
          {showAddBeta && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}`, marginTop: '8px' }}>
              <input value={newBetaUser.name} onChange={e => setNewBetaUser(p => ({ ...p, name: e.target.value }))} placeholder="Name *" style={inputStyle} autoFocus />
              <input value={newBetaUser.email} onChange={e => setNewBetaUser(p => ({ ...p, email: e.target.value }))} placeholder="Email *" style={inputStyle} />
              <select value={newBetaUser.status} onChange={e => setNewBetaUser(p => ({ ...p, status: e.target.value }))} style={selectStyle}>
                {['invited', 'active', 'churned'].map(s => <option key={s}>{s}</option>)}
              </select>
              <select
                value={newBetaUser.source}
                onChange={e => {
                  const val = e.target.value
                  const referredByUser = val.startsWith('Referred by ')
                    ? betaUsers.find(u => `Referred by ${u.name}` === val)
                    : null
                  setNewBetaUser(p => ({
                    ...p,
                    source: val,
                    referred_by: referredByUser?.name || null,
                    referred_by_user_id: referredByUser?.id || null,
                  }))
                }}
                style={selectStyle}
              >
                <option value="">Source…</option>
                <option value="Personal Network">Personal Network</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Welcome Wagon">Welcome Wagon</option>
                <option value="Cold Outreach">Cold Outreach</option>
                <option value="Organic">Organic / Found us</option>
                {betaUsers.map(u => (
                  <option key={u.id} value={`Referred by ${u.name}`}>👤 Referred by {u.name}</option>
                ))}
              </select>
              <input type="number" min="0" max="10" value={newBetaUser.nps_score} onChange={e => setNewBetaUser(p => ({ ...p, nps_score: e.target.value }))} placeholder="NPS 0–10" style={{ ...inputStyle, minWidth: '90px', flex: 'none' }} />
              <input value={newBetaUser.notes} onChange={e => setNewBetaUser(p => ({ ...p, notes: e.target.value }))} placeholder="Notes…" style={{ ...inputStyle, flex: 2 }} />
              <button onClick={addBetaUser} style={btnStyle(C.success)}>Add + Send Invite</button>
              <button onClick={() => { setShowAddBeta(false); setNewBetaUser({ name: '', email: '', status: 'invited', source: '', nps_score: '', notes: '', referred_by: null, referred_by_user_id: null }) }} style={btnStyle(C.muted)}>Cancel</button>
            </div>
          )}
        </SectionCard>

        {/* 8. Feature Voting Board */}
        <SectionCard title="💡 Feature Voting Board">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            {FEATURE_COLS.map(col => {
              const colFeatures = features.filter(fr => fr.status === col)
              const nextCol = FEATURE_COLS[FEATURE_COLS.indexOf(col) + 1]
              const colColor = FEATURE_COL_COLORS[col]
              return (
                <div key={col}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px', paddingBottom: '6px', borderBottom: `2px solid ${colColor}` }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: colColor, textTransform: 'uppercase', fontFamily: fs }}>{FEATURE_COL_LABELS[col]}</span>
                    <span style={{ fontSize: '10px', color: C.muted }}>({colFeatures.length})</span>
                  </div>
                  {colFeatures.map(feat => (
                    <div key={feat.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px', marginBottom: '6px' }}>
                      <div style={{ fontSize: '12px', color: C.brown, fontWeight: '600', marginBottom: '3px', fontFamily: fs }}>{feat.title}</div>
                      {feat.description && <div style={{ fontSize: '11px', color: C.muted, marginBottom: '5px', fontFamily: fs, lineHeight: '1.4' }}>{feat.description}</div>}
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => upvoteFeature(feat)}
                          style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: '4px', padding: '2px 7px', cursor: 'pointer', fontSize: '11px', color: C.muted, fontFamily: fs }}
                        >
                          ▲ {feat.votes || 0}
                        </button>
                        {nextCol && (
                          <button
                            onClick={() => moveFeature(feat, nextCol)}
                            style={{ background: 'none', border: `1px solid ${colColor}55`, borderRadius: '4px', padding: '2px 7px', cursor: 'pointer', fontSize: '10px', color: colColor, fontFamily: fs }}
                          >
                            → {FEATURE_COL_LABELS[nextCol]}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {colFeatures.length === 0 && (
                    <div style={{ fontSize: '11px', color: C.muted, padding: '6px 0', fontFamily: fs }}>Empty</div>
                  )}
                </div>
              )
            })}
          </div>
          {showAddFeature ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px', padding: '12px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}` }}>
              <input
                value={newFeature.title}
                onChange={e => setNewFeature(p => ({ ...p, title: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addFeature()}
                placeholder="Feature title…"
                style={inputStyle}
                autoFocus
              />
              <input
                value={newFeature.description}
                onChange={e => setNewFeature(p => ({ ...p, description: e.target.value }))}
                placeholder="Description…"
                style={{ ...inputStyle, flex: 2 }}
              />
              <button onClick={addFeature} style={btnStyle(C.success)}>Add to Backlog</button>
              <button onClick={() => setShowAddFeature(false)} style={btnStyle(C.muted)}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowAddFeature(true)} style={{ ...btnStyle(C.amber), marginTop: '14px' }}>+ Add Feature Request</button>
          )}
        </SectionCard>

        {/* 9. Advisor Notes */}
        <SectionCard title="🤝 Advisor Notes" defaultOpen={false}>
          {advisors.length === 0 && <div style={{ fontSize: '12px', color: C.muted }}>No advisors yet.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            {advisors.map((advisor, i) => (
              <div key={i} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontFamily: f, fontSize: '15px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>{advisor.name}</div>
                {advisor.email && (
                  <a href={`mailto:${advisor.email}`} style={{ fontSize: '11px', color: C.amber, display: 'block', marginBottom: '6px', fontFamily: fs, textDecoration: 'none' }}>
                    {advisor.email}
                  </a>
                )}
                {advisor.expertise && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                    {(Array.isArray(advisor.expertise) ? advisor.expertise : [advisor.expertise]).map((exp, j) => (
                      <span key={j} style={{ fontSize: '10px', background: '#FBF3E8', color: C.amber, border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '2px 7px', fontFamily: fs }}>
                        {exp}
                      </span>
                    ))}
                  </div>
                )}
                {advisor.last_contact && (
                  <div style={{ fontSize: '11px', color: C.muted, marginBottom: '6px', fontFamily: fs }}>
                    Last contact: {advisor.last_contact}
                  </div>
                )}
                {advisor.notes && (
                  <div style={{ fontSize: '12px', color: C.brown, fontFamily: fs, lineHeight: '1.6' }}>{advisor.notes}</div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Contractor Applications */}
        <SectionCard title="🔧 Contractor Applications" defaultOpen={true}>
          {pendingContractors.length === 0 ? (
            <div style={{ fontSize: '13px', color: C.muted, fontFamily: fs }}>No pending applications.</div>
          ) : pendingContractors.map(c => (
            <div key={c.id} style={{ background: C.bg, borderRadius: '10px', padding: '14px 16px', marginBottom: '10px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontFamily: f, fontSize: '15px', color: C.brown }}>{c.name}</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: fs }}>{c.email} · {c.category || 'General'} · {c.city || ''}</div>
                </div>
                <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>{new Date(c.created_at).toLocaleDateString()}</div>
              </div>
              {c.description && <div style={{ fontSize: '13px', color: C.brown, fontFamily: fs, marginBottom: '10px', lineHeight: '1.5' }}>{c.description}</div>}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => approveContractor(c.id)} style={{ padding: '6px 14px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontFamily: fs }}>Approve</button>
                <button onClick={() => rejectContractor(c.id)} style={{ padding: '6px 14px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontFamily: fs }}>Reject</button>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* Growth & Referrals */}
        <SectionCard title="📣 Growth & Referrals" defaultOpen={false}>
          {referralCodes.length === 0 ? (
            <div style={{ fontSize: '13px', color: C.muted, fontFamily: fs }}>No referral codes found.</div>
          ) : referralCodes.map(rc => (
            <div key={rc.id} style={{ background: C.bg, borderRadius: '10px', padding: '16px', marginBottom: '12px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontFamily: f, fontSize: '15px', color: C.brown }}>{rc.label || rc.code}</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: fs }}>Code: <strong>{rc.code}</strong> · {rc.scan_count || 0} scans · {rc.signup_count || 0} signups</div>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(`https://casavio-app.vercel.app/r/${rc.code}`); setCopiedId(rc.id); setTimeout(() => setCopiedId(null), 2000) }}
                  style={{ padding: '6px 12px', background: copiedId === rc.id ? '#16A34A' : C.amber, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontFamily: fs }}
                >{copiedId === rc.id ? 'Copied!' : 'Copy Link'}</button>
              </div>
              <div id={`qr-${rc.code}`} style={{ display: 'flex', justifyContent: 'center', background: '#fff', padding: '12px', borderRadius: '8px', border: `1px solid ${C.border}` }}>
                <QRCodeSVG value={`https://casavio-app.vercel.app/r/${rc.code}`} size={140} level="M" />
              </div>
              <button
                onClick={() => {
                  const wrap = document.getElementById(`qr-${rc.code}`)
                  const svg = wrap?.querySelector('svg')
                  if (svg) {
                    const xml = new XMLSerializer().serializeToString(svg)
                    const blob = new Blob([xml], { type: 'image/svg+xml' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a'); a.href = url; a.download = `casavio-ref-${rc.code}.svg`; a.click()
                    setTimeout(() => URL.revokeObjectURL(url), 1000)
                  }
                }}
                style={{ marginTop: '8px', width: '100%', padding: '8px', background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', color: C.muted, cursor: 'pointer', fontFamily: fs }}
              >⬇ Download QR</button>
            </div>
          ))}
        </SectionCard>

        {/* Bug Reports */}
        <SectionCard title={`🐛 Bug Reports (${bugReports.length})`}>
          {bugReports.length === 0 ? (
            <div style={{ color: C.muted, fontFamily: fs, fontSize: '13px' }}>No bug reports yet.</div>
          ) : bugReports.map(br => (
            <div key={br.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: C.brown, fontFamily: fs, marginBottom: '2px' }}>{br.category}</div>
                  {br.description && <div style={{ fontSize: '12px', color: C.muted, fontFamily: fs, marginBottom: '4px' }}>{br.description}</div>}
                  <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>
                    {br.profiles ? `${br.profiles.first_name || ''} ${br.profiles.last_name || ''} — ${br.profiles.email}`.trim() : br.user_id?.slice(0, 8)}
                    {' · '}{new Date(br.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <select
                  value={br.status || 'open'}
                  onChange={async e => {
                    const newStatus = e.target.value
                    await supabase.from('bug_reports').update({ status: newStatus }).eq('id', br.id)
                    setBugReports(prev => prev.map(r => r.id === br.id ? { ...r, status: newStatus } : r))
                  }}
                  style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.bg, color: C.brown, fontFamily: fs, cursor: 'pointer', flexShrink: 0 }}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="wont_fix">Won't Fix</option>
                </select>
              </div>
            </div>
          ))}
        </SectionCard>

      </div>
      )}

      {/* Drilldown Modal */}
      {drilldown && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDrilldown(null)}
        >
          <div
            style={{ background: '#FDFAF6', borderRadius: '12px', padding: '32px', width: '80%', maxWidth: '800px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontFamily: f, fontSize: '20px', color: C.brown }}>{drilldown.title}</div>
              <button onClick={() => setDrilldown(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: C.muted }}>✕</button>
            </div>
            {drilldown.note && (
              <div style={{ fontSize: '12px', color: C.muted, fontFamily: fs, background: C.bg, padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', lineHeight: '1.6' }}>
                {drilldown.note}
              </div>
            )}
            {drilldown.loading ? (
              <div style={{ color: C.muted, fontFamily: fs }}>Loading…</div>
            ) : drilldown.rows.length === 0 ? (
              <div style={{ color: C.muted, fontFamily: fs, fontSize: '13px' }}>No records found.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: fs }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                    {drilldown.columns.map(col => (
                      <th key={col} style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: '600', whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drilldown.rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      {drilldown.columns.map(col => {
                        const val = row[col.toLowerCase().replace(/ /g, '_')]
                        return (
                          <td key={col} style={{ padding: '10px 12px', color: C.brown }}>
                            {typeof val === 'string' && val.startsWith('http') ? (
                              <a href={val} target="_blank" rel="noopener noreferrer" style={{ color: C.amber, textDecoration: 'none' }}>
                                {val.length > 42 ? val.substring(0, 42) + '…' : val}
                              </a>
                            ) : (val ?? '—')}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

    </div>
  )
}
